import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';

export default function AdminDashboardPage() {
  const { stats, plans, loading, error, fetchStats } = useAdmin();

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: 'ri-user-line',
      color: 'bg-blue-500',
      link: '/admin/users'
    },
    {
      title: 'Active Subscriptions',
      value: stats?.activeSubscriptions || 0,
      icon: 'ri-vip-crown-line',
      color: 'bg-[#004D40]',
      link: '/admin/users?status=ACTIVE'
    },
    {
      title: 'New This Month',
      value: stats?.newUsersThisMonth || 0,
      icon: 'ri-user-add-line',
      color: 'bg-green-500',
      link: '/admin/users'
    },
    {
      title: 'Total Revenue',
      value: `${(stats?.totalRevenue || 0).toLocaleString()} SAR`,
      icon: 'ri-money-dollar-circle-line',
      color: 'bg-amber-500',
      link: '#'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">Admin Dashboard</h1>
              <p className="text-gray-500 mt-1">Manage users and subscriptions</p>
            </div>
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Site
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <i className="ri-error-warning-line mr-2"></i>
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <Link
              key={index}
              to={stat.link}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <i className={`${stat.icon} text-xl text-white`}></i>
                </div>
                <i className="ri-arrow-right-up-line text-gray-400"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {loading ? (
                  <span className="inline-block w-16 h-7 bg-gray-200 rounded animate-pulse"></span>
                ) : (
                  stat.value
                )}
              </div>
              <div className="text-sm text-gray-500">{stat.title}</div>
            </Link>
          ))}
        </div>

        {/* Quick Actions & Subscription Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-4">
              <Link
                to="/admin/users"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-user-search-line text-blue-600"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">View Users</div>
                  <div className="text-xs text-gray-500">Search & filter</div>
                </div>
              </Link>
              <Link
                to="/admin/developers"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <i className="ri-building-4-line text-amber-600"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Developers</div>
                  <div className="text-xs text-gray-500">Verify & manage</div>
                </div>
              </Link>
              <Link
                to="/admin/investor-leads"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-10 h-10 bg-[#DDEEEB] rounded-lg flex items-center justify-center">
                  <i className="ri-mail-star-line text-[#003930]"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Investor Leads</div>
                  <div className="text-xs text-gray-500">Apply-form queue</div>
                </div>
              </Link>
              <Link
                to="/admin/users?status=ACTIVE"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-10 h-10 bg-[var(--pv-color-surface-sunken)] rounded-lg flex items-center justify-center">
                  <i className="ri-vip-crown-2-line text-[#004D40]"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Subscribers</div>
                  <div className="text-xs text-gray-500">Active plans</div>
                </div>
              </Link>
              <Link
                to="/admin/users?status=EXPIRED"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-red-600"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Expired</div>
                  <div className="text-xs text-gray-500">Need renewal</div>
                </div>
              </Link>
              <Link
                to="/membership"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-green-600"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">View Plans</div>
                  <div className="text-xs text-gray-500">Pricing page</div>
                </div>
              </Link>
            </div>
          </div>

          {/* Subscription Distribution */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-4">Subscriptions by Plan</h2>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {plans.map((plan) => {
                  const count = stats?.subscriptionsByPlan?.[plan.id] || 0;
                  const total = stats?.activeSubscriptions || 1;
                  const percentage = Math.round((count / total) * 100) || 0;

                  return (
                    <div key={plan.id} className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{plan.name}</span>
                          <span className="text-sm text-gray-500">{count} users</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#004D40] rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {plans.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <i className="ri-pie-chart-line text-4xl mb-2 block"></i>
                    No subscription data available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Links */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] mb-4">Admin Sections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/admin/users"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-[var(--pv-color-accent-soft)] hover:bg-[var(--pv-color-surface-sunken)] transition-colors group"
            >
              <div className="w-12 h-12 bg-[var(--pv-color-surface-sunken)] rounded-xl flex items-center justify-center group-hover:bg-[#DDEEEB] transition-colors">
                <i className="ri-group-line text-xl text-[#004D40]"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">User Management</div>
                <div className="text-sm text-gray-500">View all users & subscriptions</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-[#004D40]"></i>
            </Link>
            <Link
              to="/admin/subscriptions/tiers"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <i className="ri-stack-line text-xl text-blue-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Subscription Tiers</div>
                <div className="text-sm text-gray-500">Manage pricing plans</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-blue-600"></i>
            </Link>
            <Link
              to="/admin/subscriptions/promo-codes"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <i className="ri-coupon-line text-xl text-green-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Promo Codes</div>
                <div className="text-sm text-gray-500">Create & manage discounts</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-green-600"></i>
            </Link>
            <Link
              to="/admin/neighborhoods"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-[var(--pv-color-accent-soft)] hover:bg-[var(--pv-color-surface-sunken)] transition-colors group"
            >
              <div className="w-12 h-12 bg-[var(--pv-color-surface-sunken)] rounded-xl flex items-center justify-center group-hover:bg-[#DDEEEB] transition-colors">
                <i className="ri-map-pin-2-line text-xl text-[#004D40]"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Neighborhood Intelligence</div>
                <div className="text-sm text-gray-500">Area stats, demand, agents & local news</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-[#004D40]"></i>
            </Link>
            <Link
              to="/admin/inspections"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-[var(--pv-color-accent-soft)] hover:bg-[var(--pv-color-surface-sunken)] transition-colors group"
            >
              <div className="w-12 h-12 bg-[var(--pv-color-surface-sunken)] rounded-xl flex items-center justify-center group-hover:bg-[#DDEEEB] transition-colors">
                <i className="ri-shield-check-line text-xl text-[#004D40]"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Broker Inspections</div>
                <div className="text-sm text-gray-500">Trusted / True badge field visits</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-[#004D40]"></i>
            </Link>
            <Link
              to="/admin/subscriptions/analytics"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                <i className="ri-bar-chart-box-line text-xl text-amber-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Analytics</div>
                <div className="text-sm text-gray-500">Revenue & subscription stats</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-amber-600"></i>
            </Link>
            <Link
              to="/admin/subscriptions/users"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                <i className="ri-user-settings-line text-xl text-indigo-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">User Subscriptions</div>
                <div className="text-sm text-gray-500">Assign tiers to users</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-indigo-600"></i>
            </Link>
            <Link
              to="/admin/subscriptions/addons"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                <i className="ri-puzzle-line text-xl text-teal-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Add-ons</div>
                <div className="text-sm text-gray-500">Manage the add-on catalog</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-teal-600"></i>
            </Link>
            <Link
              to="/admin/subscriptions/addon-orders"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-cyan-300 hover:bg-cyan-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                <i className="ri-shopping-bag-line text-xl text-cyan-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Add-on Orders</div>
                <div className="text-sm text-gray-500">Fulfill & manage purchases</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-cyan-600"></i>
            </Link>
            <Link
              to="/admin/subscriptions/bundles"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-fuchsia-300 hover:bg-fuchsia-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-fuchsia-100 rounded-xl flex items-center justify-center group-hover:bg-fuchsia-200 transition-colors">
                <i className="ri-gift-line text-xl text-fuchsia-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Bundles</div>
                <div className="text-sm text-gray-500">Build & manage add-on bundles</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-fuchsia-600"></i>
            </Link>
            <Link
              to="/admin/subscriptions/coins"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                <i className="ri-copper-coin-line text-xl text-amber-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Coins</div>
                <div className="text-sm text-gray-500">Coin rate & user balances</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-amber-600"></i>
            </Link>
            <Link
              to="/admin/complaints"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-rose-300 hover:bg-rose-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center group-hover:bg-rose-200 transition-colors">
                <i className="ri-feedback-line text-xl text-rose-600"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">Complaints</div>
                <div className="text-sm text-gray-500">Manage user complaints</div>
              </div>
              <i className="ri-arrow-right-line ml-auto text-gray-400 group-hover:text-rose-600"></i>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
